import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Tab } from '@mui/material';
import { CurrentKnowledgeCallouts } from 'app/features/callout/calloutSlice';
import { useAppSelector } from 'app/store';
import { MprcContainer, MprcIconButtonDropdown, MprcRequestErrorReport, MprcText } from 'components';
import { MprcFilterBox, MprcFilterCountInfo, MprcFilterLine } from 'components/filter';
import { IconSort } from 'components/icons/IconSort';
import { MprcChipTabs } from 'components/MprcChipTabs';
import { MprcTextFieldColor } from 'components/MprcTextFieldColor';
import { FeedDataDto, FeedQuestionViewAs, FeedType } from 'generated/CoreApi_v1';
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { FeedContainer } from '../components';
import { FeedManager, FeedManagerHandle } from '../components/FeedManager';
import { FeedCustomEventProps, subscribe, unsubscribe } from '../FeedEvents';

////////////////////////////////////////////////////////////////////////////////

type QuestionFeedType = FeedType.QuestionRecent | FeedType.QuestionToYou | FeedType.QuestionFollowing;

interface IFilter {
    searchText: string;
    feedType: QuestionFeedType;
    viewAs: FeedQuestionViewAs;
}

interface ISearchText {
    value: string;
    focused: boolean;
}

interface MenuOption {
    feedType: QuestionFeedType;
    resourceName: string;
}

////////////////////////////////////////////////////////////////////////////////

const menuItems: MenuOption[] = [
    { feedType: FeedType.QuestionRecent, resourceName: "MoreRecentFilterText" },
    { feedType: FeedType.QuestionToYou, resourceName: "ForYouFilterText" },
    { feedType: FeedType.QuestionFollowing, resourceName: "FollowingFilterText" }
];

////////////////////////////////////////////////////////////////////////////////

export const FeedQuestionAndAnswers: React.FC = () => {
    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const callouts = useAppSelector(CurrentKnowledgeCallouts);

    const location = useLocation();
    const navigate = useNavigate();

    const [searchText, setSearchText] = useState<ISearchText>({ focused: false, value: location.state?.filter?.searchText ?? "" });
    const [loading, setLoading] = useState<boolean>(true);
    const [totalItems, setTotalItems] = useState<number | null | undefined>();
    const [prevPub, setPrevPub] = useState<string | null>(location.state?.publicationId ?? null);
    const [viewError, setViewError] = useState<any>();

    const [filterOptions, setFilterOptions] = useState<IFilter>(location.state?.filter ?? {
        searchText: "",
        feedType: FeedType.QuestionRecent,
        viewAs: FeedQuestionViewAs.All
    });

    const searchRef = useRef<HTMLInputElement | undefined>(undefined);
    const feedRef = useRef<FeedManagerHandle>(undefined!);
    const filtered = filterOptions.searchText.length > 0;

    ////////////////////////////////////////////////////////////////////////////////

    ///
    const handleNewItem = (e: FeedCustomEventProps) => {
        if (filterOptions.feedType == FeedType.QuestionRecent && filterOptions.searchText == "" && filterOptions.viewAs == FeedQuestionViewAs.All && !feedRef.current.scrolled())
            feedRef.current.reset();
    };

    ///
    const onGetData = async (startIndex: number, endIndex: number) => {
        setLoading(true);

        let feedData: FeedDataDto = { items: [], totalRows: 0 };

        if (filterOptions.feedType == FeedType.QuestionRecent) {
            let result = await api.v1.knowledgeFeed.getFeedQuestionRecent({ startIndex, startPublicationId: location.state?.publicationId ?? null, endIndex, ...filterOptions });
            feedData = result.data;
        }
        else if (filterOptions.feedType == FeedType.QuestionToYou) {
            let result = await api.v1.knowledgeFeed.getFeedQuestionToYou({ startIndex, endIndex, startPublicationId: location.state?.publicationId ?? null, ...filterOptions });
            feedData = result.data;
        }
        else if (filterOptions.feedType == FeedType.QuestionFollowing) {
            let result = await api.v1.knowledgeFeed.getFeedQuestionFollowing({ startIndex, endIndex, startPublicationId: location.state?.publicationId ?? null, ...filterOptions });
            feedData = result.data;
        }

        setTotalItems(feedData.totalRows);
        setLoading(false);
        return feedData.items;
    };

    ///
    const handleFilterChange = (filter: IFilter) => {
        if (!filter.searchText) setSearchText({ ...searchText, value: "" });
        setFilterOptions(filter);
        setTotalItems(null);
    };

    ///
    const handleChoise = (option: QuestionFeedType) => {
        handleFilterChange({ ...filterOptions, feedType: option, searchText: "", viewAs: FeedQuestionViewAs.All });
    };

    ///
    const resetFilters = () => {
        handleFilterChange({ ...filterOptions, searchText: "", viewAs: FeedQuestionViewAs.All });
    };


    ///
    useEffect(() => {
        if (totalItems === null) {
            navigate(document.location.search, { replace: true, state: { filter: filterOptions } });
        }
    }, [totalItems]);

    ///
    useEffect(() => {
        subscribe("QuestionCreated", handleNewItem);
        return () => {
            unsubscribe("QuestionCreated", handleNewItem);
        };
    }, [filterOptions]);

    ///
    useDidUpdateEffect(() => {
        if (prevPub !== location.state?.publicationId) {
            setPrevPub(null);
        }
        if (location.state != null) feedRef.current?.reset();
    }, [location.state]);

    /// Retry
    useDidUpdateEffect(() => {
        if (viewError == null) feedRef.current.reset();
    }, [viewError]);

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    const Header = (
        <Box>
            <MprcContainer>
                <MprcFilterBox>
                    <MprcFilterLine>
                        <MprcChipTabs
                            value={filterOptions.feedType}
                            onChange={(e, value) => handleChoise(value)}
                        >
                            {
                                [menuItems.map(item =>
                                    <Tab
                                        key={item.feedType}
                                        label={
                                            <span>
                                                {(callouts ?? []).find(x => item.feedType == FeedType[x.id as keyof typeof FeedType])?.active === true &&
                                                    <Box sx={{ mr: 1 }} component="span">
                                                        <MprcText color="warning" >
                                                            <FontAwesomeIcon icon={faCircle} size="xs" color="warning" />
                                                        </MprcText>
                                                    </Box>
                                                }
                                                {t(`Feed.QuestionAndAnswers.${item.resourceName}`)}
                                            </span>
                                        }
                                        value={item.feedType}
                                    />)
                                ]
                            }
                        </MprcChipTabs>
                    </MprcFilterLine>

                    <MprcFilterLine>
                        <MprcTextFieldColor
                            fullWidth
                            value={searchText.value}
                            onChange={e => setSearchText({ ...searchText, value: e.target.value })}
                            onBlur={() => setSearchText({ ...searchText, focused: false })}
                            onFocus={() => setSearchText({ ...searchText, focused: true })}
                            inputRef={searchRef}
                            autoFocus
                            autoComplete="off"
                            onKeyDown={(e) => {
                                if (e.keyCode == 13) handleFilterChange({ ...filterOptions, searchText: searchRef.current?.value ?? "" });
                            }}
                            placeholder={t("Feed.QuestionAndAnswers.SearchTextFocusPlaceholder")}
                        />
                        <MprcIconButtonDropdown
                            menuItemCheck
                            color={filterOptions.viewAs != FeedQuestionViewAs.All ? "warning" : undefined}
                            icon={<IconSort />}
                            title={t("shared:Filter.OrderBy")}
                            selected={filterOptions.viewAs}
                            onChange={(questionType) => {
                                handleFilterChange({ ...filterOptions, viewAs: questionType });
                            }}
                            items={[
                                { text: t("Feed.QuestionAndAnswers.AllQuestionsText"), value: FeedQuestionViewAs.All },
                                { text: t("Feed.QuestionAndAnswers.WithoutAnswersText"), value: FeedQuestionViewAs.WithoutAnswers },
                                { text: t("Feed.QuestionAndAnswers.WithAnswersText"), value: FeedQuestionViewAs.WithAnswers },
                            ]}
                        />
                    </MprcFilterLine>

                </MprcFilterBox>
            </MprcContainer>
            {
                loading
                    ? null
                    : <MprcFilterCountInfo
                        text={t(filtered ? "Feed.QuestionAndAnswers.RecordsFoundText" : "Feed.QuestionAndAnswers.RecordsText", { count: totalItems ?? 0 })}
                        variant={filtered ? "bodyNotice" : (totalItems ?? 0) == 0 ? "captionHeader" : "caption"}
                        onResetFilter={filtered ? resetFilters : undefined}
                    />
            }
        </Box >
    );

    return viewError
        ? <MprcRequestErrorReport
            error={viewError}
            onRetry={() => setViewError(null)}
        />
        : <FeedContainer
            header={Header}
            feed={
                <FeedManager
                    feedType={filterOptions.feedType}
                    previousPublicationId={prevPub}
                    onGobackTop={() => setTotalItems(null)}
                    onGetData={onGetData}
                    onError={(error) => setViewError(error)}
                    ref={feedRef}
                    useInfiniteLoading
                />
            }
        />;
};
